'use strict';

/**
 * Bulk File Upload — data/logic provider (ProjectWorkspace ▸ Data ▸ Bulk upload files).
 *
 * Stages many browser Files and creates ONE Observation per file (image/* → an Image observation, video/* → Video,
 * audio/* → Audio, everything else → a File observation), reusing the same server pipeline the Important Files
 * single-upload proved out (create the Observation → the server provisions its Artifact → POST the raw bytes) but
 * WITHOUT the ProjectFile pointer step (these are plain project observations, not important-file listings).
 *
 * The user edits each row's Name + Designated Date (Observation.DesignatedTime) in a grid before uploading (the
 * values are baked into the POST), and may keep editing after — a post-upload edit does a full-record PUT so it
 * persists. Uploads run through a small concurrency pool with a per-row progress callback + a byte-weighted
 * overall bar. All state lives on the provider (rows) + AppData.BulkUpload (view flags); the view paints the grid
 * and pokes per-row/overall progress into the DOM (no full re-render mid-upload, so inputs keep focus).
 */

const libPictProvider = require('pict-provider');

// MIME → Headlight ObservationType. Media carry the legacy prefix; everything else is a plain 'File'. The datum
// key is the type without the prefix ('Image'/'Video'/'Audio'/'File'). Mirrors ImportantFiles-Provider.
const OBSERVATION_TYPE_FOR_MIME = (pMime) =>
{
	const tmpMime = String(pMime || '').toLowerCase();
	if (tmpMime.indexOf('image/') === 0) { return 'Legacy Image'; }
	if (tmpMime.indexOf('video/') === 0) { return 'Legacy Video'; }
	if (tmpMime.indexOf('audio/') === 0) { return 'Legacy Audio'; }
	return 'File';
};

// The artifact-type name inside the observation's Details differs from the datum key for plain files: a 'File'
// observation's artifact MUST be 'Data_File' (the server's Data_File manifest validates it — sending 'File'
// leaves the artifact Invalid=1). Media types use their datum name as-is. (Verified against real observations.)
const ARTIFACT_TYPE_FOR_DATUM = { File: 'Data_File', Image: 'Image', Video: 'Video', Audio: 'Audio' };

class BulkUploadProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
		this._idProject = 0;
		this._rows = [];
		this._rowSeq = 0;
		this._uploading = false;
	}

	get restClient() { return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient; }
	get view() { return this.pict.views['BulkUpload']; }
	get rows() { return this._rows; }
	get idProject() { return this._idProject; }
	get isUploading() { return this._uploading; }

	/** The screen's AppData-backed flags (the view derives its slots from these). */
	get state()
	{
		if (!this.pict.AppData.BulkUpload)
		{
			this.pict.AppData.BulkUpload = { IDProject: 0, HasRows: false, Uploading: false };
		}
		return this.pict.AppData.BulkUpload;
	}

	_render() { if (this.view) { this.view.render(); } }

	// --- Capability gate (Observation/Artifact creation) --------------------------------------------

	get permissions() { return this.pict.fable.EffectivePermissionsService; }

	/** Server-authoritative capability check, fail-CLOSED (no service / unloaded map → false) so the upload
	 *  affordance never flashes before we know the user's grants. Super-users pass (the service bypasses). */
	can(pEntity, pAction)
	{
		const tmpPermissions = this.permissions;
		if (!tmpPermissions || (typeof tmpPermissions.can !== 'function') || !tmpPermissions.currentUserReady()) { return false; }
		return tmpPermissions.can(pEntity, pAction);
	}

	/** May the current user bulk-create observations? Needs Observation-Create (the artifact rides along on the
	 *  observation POST, which the server authorizes together). */
	canUpload() { return this.can('Observation', 'Create'); }

	// --- Lifecycle / embed --------------------------------------------------------------------------

	/** Point the tool at a project. Switching projects clears any staged rows (they belong to the old project). */
	setProject(pIDProject)
	{
		const tmpID = Number(pIDProject) || 0;
		if (tmpID !== this._idProject)
		{
			this._idProject = tmpID;
			this._rows = [];
			this._uploading = false;
		}
		this.state.IDProject = this._idProject;
		this.state.HasRows = (this._rows.length > 0);
		this.state.Uploading = this._uploading;
	}

	// --- Row staging --------------------------------------------------------------------------------

	/** 'report.pdf' → 'report'; leaves extension-less names alone. Used as the default observation Name. */
	_stripExtension(pName)
	{
		const tmpName = String(pName == null ? '' : pName).trim();
		return tmpName.replace(/\.[A-Za-z0-9]{1,8}$/, '') || tmpName || 'Untitled';
	}

	/** A file's own last-modified day as YYYY-MM-DD (a sensible default Designated Date for old scanned docs);
	 *  falls back to today. UTC date parts so it round-trips with how we serialize DesignatedTime. */
	_defaultDateISO(pFile)
	{
		let tmpDate = null;
		if (pFile && pFile.lastModified) { tmpDate = new Date(pFile.lastModified); }
		if (!tmpDate || Number.isNaN(tmpDate.getTime())) { tmpDate = new Date(); }
		return tmpDate.toISOString().slice(0, 10);
	}

	/** Add browser Files as staged rows. Returns the number added. */
	addFiles(pFiles)
	{
		const tmpFiles = Array.prototype.slice.call(pFiles || []);
		let tmpAdded = 0;
		tmpFiles.forEach((pFile) =>
		{
			if (!pFile) { return; }
			const tmpObsType = OBSERVATION_TYPE_FOR_MIME(pFile.type);
			this._rows.push(
				{
					UID: `blk_${++this._rowSeq}`,
					File: pFile,
					Name: this._stripExtension(pFile.name || ''),
					FileName: pFile.name || 'file',
					DateISO: this._defaultDateISO(pFile),
					Mime: pFile.type || '',
					ObservationType: tmpObsType,
					Type: tmpObsType.replace(/^Legacy\s+/, ''),   // Image / Video / Audio / File
					Size: pFile.size || 0,
					Status: 'staged',
					Progress: 0,
					UploadedBytes: 0,
					IDObservation: 0,
					Message: '',
				});
			tmpAdded++;
		});
		if (tmpAdded) { this.state.HasRows = (this._rows.length > 0); this._render(); }
		return tmpAdded;
	}

	_findRow(pUID) { return this._rows.find((pRow) => pRow.UID === pUID) || null; }

	/** Remove one row (only when not mid-upload). */
	removeRow(pUID)
	{
		const tmpRow = this._findRow(pUID);
		if (!tmpRow || tmpRow.Status === 'uploading') { return; }
		this._rows = this._rows.filter((pRow) => pRow.UID !== pUID);
		this.state.HasRows = (this._rows.length > 0);
		this._render();
	}

	/** Clear every row (blocked while uploading). */
	clearAll()
	{
		if (this._uploading) { return; }
		this._rows = [];
		this.state.HasRows = false;
		this._render();
	}

	/** Edit a staged row's Name; a done row's edit is persisted with a full-record PUT. */
	async setRowName(pUID, pName)
	{
		const tmpRow = this._findRow(pUID);
		if (!tmpRow) { return; }
		tmpRow.Name = String(pName == null ? '' : pName);
		if (tmpRow.Status === 'done' && tmpRow.IDObservation) { await this._persistRowEditAsync(tmpRow); }
	}

	/** Edit a row's Designated Date (YYYY-MM-DD); a done row's edit is persisted with a full-record PUT. */
	async setRowDate(pUID, pDateISO)
	{
		const tmpRow = this._findRow(pUID);
		if (!tmpRow) { return; }
		tmpRow.DateISO = String(pDateISO == null ? '' : pDateISO);
		if (tmpRow.Status === 'done' && tmpRow.IDObservation) { await this._persistRowEditAsync(tmpRow); }
	}

	// --- Upload pipeline ----------------------------------------------------------------------------

	_postJSON(pURL, pBody) { return new Promise((resolve, reject) => this.restClient.postJSON(pURL, pBody, (pErr, pRes) => (pErr ? reject(pErr) : resolve(pRes)))); }
	_getJSON(pURL) { return new Promise((resolve, reject) => this.restClient.getJSON(pURL, (pErr, pRes) => (pErr ? reject(pErr) : resolve(pRes)))); }
	_putJSON(pURL, pBody) { return new Promise((resolve, reject) => this.restClient.putJSON(pURL, pBody, (pErr, pRes) => (pErr ? reject(pErr) : resolve(pRes)))); }
	_postBinary(pURL, pFile, pOnProgress) { return new Promise((resolve, reject) => this.restClient.postBinary(pURL, pFile, pFile.type || 'application/octet-stream', (pErr, pBody) => (pErr ? reject(pErr) : resolve(pBody)), pOnProgress)); }

	/**
	 * Upload raw bytes with LIVE progress. The rest client's postBinary buffers the whole File (arrayBuffer) and
	 * fires its progress callback only ONCE at completion, so a real per-file bar needs `XMLHttpRequest.upload.
	 * onprogress`. We POST straight to the same-origin media route with `withCredentials` — the config-app rest
	 * client authenticates purely by the ambient session cookie (it sets no SessionToken/Bearer header), so the
	 * cookie rides along and the request is identical to postBinary's, just observable. Falls back to postBinary
	 * when XHR upload progress isn't available (non-browser / no upload object).
	 * @param {String} pURL rest path (e.g. 'Artifact/Media/123/1')
	 * @param {File} pFile
	 * @param {Function} pOnProgress receives a ProgressEvent (lengthComputable/loaded/total)
	 */
	_uploadBytesAsync(pURL, pFile, pOnProgress)
	{
		if ((typeof XMLHttpRequest === 'undefined') || !this.restClient || (typeof this.restClient.formatUrl !== 'function'))
		{
			return this._postBinary(pURL, pFile, pOnProgress);
		}
		return new Promise((resolve, reject) =>
		{
			let tmpXHR = null;
			try { tmpXHR = new XMLHttpRequest(); } catch (pErr) { tmpXHR = null; }
			if (!tmpXHR || !tmpXHR.upload) { this._postBinary(pURL, pFile, pOnProgress).then(resolve, reject); return; }
			tmpXHR.open('POST', this.restClient.formatUrl(pURL), true);
			tmpXHR.withCredentials = true;   // send the ambient UserSession cookie (same-origin /1.0/)
			try { tmpXHR.setRequestHeader('Content-Type', pFile.type || 'application/octet-stream'); } catch (pErr) { /* header already fixed by the agent in rare states */ }
			tmpXHR.upload.onprogress = (pEvent) => { if (typeof pOnProgress === 'function') { pOnProgress(pEvent); } };
			tmpXHR.onload = () =>
			{
				if (tmpXHR.status >= 200 && tmpXHR.status < 300) { resolve(tmpXHR.responseText); }
				else { reject(new Error(`Upload failed (HTTP ${tmpXHR.status || 0}).`)); }
			};
			tmpXHR.onerror = () => reject(new Error('Upload failed — network error.'));
			tmpXHR.onabort = () => reject(new Error('Upload cancelled.'));
			try { tmpXHR.send(pFile); } catch (pErr) { reject(pErr); }
		});
	}

	/** A row's Designated Date as a DesignatedTime datetime. Noon UTC so the calendar day doesn't shift under a
	 *  timezone when it round-trips (we read the day back off the ISO date part). Falls back to now. */
	_designatedTimeFromRow(pRow)
	{
		const tmpISO = String(pRow.DateISO || '').trim();
		if (/^\d{4}-\d{2}-\d{2}$/.test(tmpISO)) { return `${tmpISO}T12:00:00.000Z`; }
		return new Date().toISOString();
	}

	/** Percent (0-100) out of whatever postBinary hands the progress callback, or null when not computable. */
	_progressPercent(pEvent)
	{
		if (pEvent == null) { return null; }
		if (typeof pEvent === 'number') { return (pEvent <= 1) ? Math.round(pEvent * 100) : Math.round(pEvent); }
		if (pEvent.lengthComputable && pEvent.total) { return Math.round((pEvent.loaded / pEvent.total) * 100); }
		if (typeof pEvent.percent === 'number') { return Math.round(pEvent.percent); }
		if ((typeof pEvent.loaded === 'number') && (typeof pEvent.total === 'number') && pEvent.total) { return Math.round((pEvent.loaded / pEvent.total) * 100); }
		return null;
	}

	/** Create the Observation for a row (server provisions its Artifact). Returns { IDObservation, IDArtifact, Version, datumType }. */
	async _createObservationAsync(pRow)
	{
		const tmpDatumType = pRow.ObservationType.replace(/^Legacy\s+/, '');
		const tmpArtifactType = ARTIFACT_TYPE_FOR_DATUM[tmpDatumType] || tmpDatumType;
		const tmpName = String(pRow.Name || '').trim() || this._stripExtension(pRow.FileName) || `New ${tmpDatumType}`;
		const tmpObservation = {
			Name: tmpName, Description: '', Tags: [], ObservationType: pRow.ObservationType,
			DesignatedTime: this._designatedTimeFromRow(pRow),
			Details: { [tmpDatumType]: [ { ArtifactType: tmpArtifactType, IDArtifact: 0, Datum: { FileName: pRow.FileName, FileMimeType: pRow.Mime || '' } } ] },
			IDObservation: 0, IDProject: this._idProject,
			CaptureLatitude: '0.00000000', CaptureLongitude: '0.00000000',
			UserLatitude: '0.00000000', UserLongitude: '0.00000000',
			AlignmentStation: 0, AlignmentOffset: 0,
		};
		const tmpNewObs = await this._postJSON('Observation', tmpObservation);
		const tmpArtifact = tmpNewObs && tmpNewObs.Details && tmpNewObs.Details[tmpDatumType] && tmpNewObs.Details[tmpDatumType][0];
		if (!tmpNewObs || !tmpNewObs.IDObservation || !tmpArtifact || !tmpArtifact.IDArtifact)
		{
			throw new Error('Could not create the observation for this file.');
		}
		return { IDObservation: tmpNewObs.IDObservation, IDArtifact: tmpArtifact.IDArtifact, Version: tmpArtifact.Version, datumType: tmpDatumType };
	}

	/** Upload one staged (or previously-errored) row: create observation → POST bytes, driving row + overall
	 *  progress through pOnTick(). Marks the row done or error; never throws (errors land on the row). */
	async _uploadRowAsync(pRow, pOnTick)
	{
		if (!pRow || !pRow.File) { return; }
		pRow.Status = 'uploading';
		pRow.Progress = 0;
		pRow.UploadedBytes = 0;
		pRow.Message = '';
		if (this.view) { this.view.paintRow(pRow.UID); }
		try
		{
			const tmpArt = await this._createObservationAsync(pRow);
			pRow.IDObservation = tmpArt.IDObservation;
			// The observation POST is a small slice of the work; the bytes are the rest. Start the bar at 3%.
			pRow.Progress = 3;
			pRow.UploadedBytes = Math.round(pRow.Size * 0.03);
			if (this.view) { this.view.paintRow(pRow.UID); }
			if (typeof pOnTick === 'function') { pOnTick(); }

			await this._uploadBytesAsync(`Artifact/Media/${tmpArt.IDArtifact}/${tmpArt.Version}`, pRow.File, (pEvent) =>
			{
				const tmpPct = this._progressPercent(pEvent);
				if (tmpPct !== null)
				{
					pRow.Progress = Math.max(3, Math.min(99, 3 + Math.round(tmpPct * 0.97)));
					pRow.UploadedBytes = Math.round(pRow.Size * (pRow.Progress / 100));
					if (this.view) { this.view.paintRow(pRow.UID); }
					if (typeof pOnTick === 'function') { pOnTick(); }
				}
			});

			pRow.Status = 'done';
			pRow.Progress = 100;
			pRow.UploadedBytes = pRow.Size;
			pRow.File = null;   // free the bytes; metadata + IDObservation remain for post-edit
		}
		catch (pError)
		{
			pRow.Status = 'error';
			pRow.Message = (pError && pError.message) ? pError.message : 'Upload failed.';
			this.pict.log.error(`BulkUpload: row ${pRow.UID} failed: ${pRow.Message}`, pError);
		}
		if (this.view) { this.view.paintRow(pRow.UID); }
		if (typeof pOnTick === 'function') { pOnTick(); }
	}

	/**
	 * Upload every staged/errored row through a small concurrency pool (retries errored rows too). Per-row progress
	 * lands via the view's paintRow; overall progress via paintOverall. Returns a { done, failed, total } summary.
	 */
	async uploadAllAsync()
	{
		if (this._uploading) { return null; }
		if (!this.canUpload())
		{
			const tmpModal = this.pict.views['Pict-Section-Modal'];
			if (tmpModal && tmpModal.toast) { tmpModal.toast('You don’t have permission to create observations on this project.', { type: 'error', duration: 6000 }); }
			return null;
		}
		const tmpPending = this._rows.filter((pRow) => (pRow.Status === 'staged') || (pRow.Status === 'error'));
		if (!tmpPending.length) { return null; }

		this._uploading = true;
		this.state.Uploading = true;
		if (this.view) { this.view.paintToolbar(); }
		const fTick = () => { if (this.view) { this.view.paintOverall(); } };
		fTick();

		const tmpConcurrency = Math.min(3, tmpPending.length);
		let tmpIndex = 0;
		const fWorker = async () =>
		{
			while (tmpIndex < tmpPending.length)
			{
				const tmpRow = tmpPending[tmpIndex++];
				await this._uploadRowAsync(tmpRow, fTick);
			}
		};
		await Promise.all(Array.from({ length: tmpConcurrency }, fWorker));

		this._uploading = false;
		this.state.Uploading = false;
		const tmpDone = this._rows.filter((pRow) => pRow.Status === 'done').length;
		const tmpFailed = this._rows.filter((pRow) => pRow.Status === 'error').length;
		this._render();   // full repaint: final states + re-enabled toolbar + summary
		const tmpModal = this.pict.views['Pict-Section-Modal'];
		if (tmpModal && tmpModal.toast)
		{
			if (!tmpFailed) { tmpModal.toast(`Uploaded ${tmpDone} observation${tmpDone === 1 ? '' : 's'}.`, { type: 'success' }); }
			else if (tmpDone) { tmpModal.toast(`Uploaded ${tmpDone}; ${tmpFailed} failed — fix and retry.`, { type: 'warning', duration: 7000 }); }
			else { tmpModal.toast(`All ${tmpFailed} uploads failed.`, { type: 'error', duration: 7000 }); }
		}
		return { done: tmpDone, failed: tmpFailed, total: this._rows.length };
	}

	/** Persist a post-upload Name/DesignatedTime edit to an already-created observation (full-record PUT). */
	async _persistRowEditAsync(pRow)
	{
		try
		{
			const tmpObs = await this._getJSON(`Observation/${pRow.IDObservation}`);
			if (!tmpObs || !tmpObs.IDObservation) { throw new Error('Observation not found.'); }
			const tmpName = String(pRow.Name || '').trim() || tmpObs.Name;
			await this._putJSON('Observation', Object.assign({}, tmpObs, { Name: tmpName, DesignatedTime: this._designatedTimeFromRow(pRow) }));
		}
		catch (pError)
		{
			this.pict.log.error(`BulkUpload: could not update observation ${pRow.IDObservation}: ${pError && pError.message ? pError.message : pError}`, pError);
			const tmpModal = this.pict.views['Pict-Section-Modal'];
			if (tmpModal && tmpModal.toast) { tmpModal.toast('Could not save that change — try again.', { type: 'error', duration: 5000 }); }
		}
	}

	// --- Overall progress accounting (byte-weighted) ------------------------------------------------

	/** { totalBytes, uploadedBytes, percent, done, failed, uploading, total } for the overall bar + counts. */
	overallStats()
	{
		let tmpTotal = 0;
		let tmpUploaded = 0;
		let tmpDone = 0;
		let tmpFailed = 0;
		let tmpUploading = 0;
		this._rows.forEach((pRow) =>
		{
			const tmpSize = Math.max(pRow.Size || 0, 1);   // guard zero-byte files so they still weigh in
			tmpTotal += tmpSize;
			if (pRow.Status === 'done') { tmpUploaded += tmpSize; tmpDone++; }
			else if (pRow.Status === 'error') { tmpFailed++; }
			else if (pRow.Status === 'uploading') { tmpUploaded += Math.min(tmpSize, pRow.UploadedBytes || 0); tmpUploading++; }
		});
		const tmpPercent = tmpTotal > 0 ? Math.round((tmpUploaded / tmpTotal) * 100) : 0;
		return { totalBytes: tmpTotal, uploadedBytes: tmpUploaded, percent: tmpPercent, done: tmpDone, failed: tmpFailed, uploading: tmpUploading, total: this._rows.length };
	}
}

module.exports = BulkUploadProvider;
module.exports.default_configuration = { ProviderIdentifier: 'BulkUpload' };
